import { useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { CircleCheck, ExternalLink, MessageSquareHeart, Send } from "lucide-react";
import { SITE } from "../config/site";
import { QUESTION_BY_ID } from "../data/questions";
import { useDocumentTitle } from "../components/ui";
import { githubIssueUrl, MAX_MESSAGE, sendFeedback, validateFeedback, type FeedbackInput, type FeedbackKind } from "../lib/feedback";
import { pick, useLang, useT, type L } from "../i18n";

const KINDS: { id: FeedbackKind; label: L }[] = [
  { id: "question", label: { vi: "Lỗi câu hỏi / đáp án", en: "Question or answer issue" } },
  { id: "content", label: { vi: "Thông tin chưa chính xác / lỗi thời", en: "Inaccurate or outdated info" } },
  { id: "bug", label: { vi: "Lỗi giao diện / chức năng", en: "Bug in the app" } },
  { id: "idea", label: { vi: "Đề xuất tính năng", en: "Feature idea" } },
  { id: "other", label: { vi: "Khác", en: "Other" } },
];

const ERRORS: Record<string, L> = {
  "too-short": { vi: "Nội dung cần ít nhất 10 ký tự.", en: "Please write at least 10 characters." },
  "too-long": { vi: `Nội dung tối đa ${MAX_MESSAGE} ký tự.`, en: `Maximum ${MAX_MESSAGE} characters.` },
  "bad-email": { vi: "Email không hợp lệ.", en: "That email doesn't look valid." },
  send: { vi: "Gửi không thành công. Bạn có thể gửi qua GitHub bên dưới.", en: "Couldn't send. You can submit through GitHub below." },
};

function isKind(v: string | null): v is FeedbackKind {
  return KINDS.some((k) => k.id === v);
}

export function FeedbackPage() {
  const t = useT();
  const lang = useLang();
  useDocumentTitle(t("Góp ý", "Feedback"));
  const [params] = useSearchParams();
  const location = useLocation();
  const qParam = params.get("q") ?? "";
  const question = qParam ? QUESTION_BY_ID.get(qParam) : undefined;
  const kParam = params.get("kind");

  const [kind, setKind] = useState<FeedbackKind>(isKind(kParam) ? kParam : question ? "question" : "idea");
  const [message, setMessage] = useState("");
  const [email, setEmail] = useState("");
  const [honeypot, setHoneypot] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<"idle" | "sending" | "sent">("idle");

  const input: FeedbackInput = {
    kind,
    message,
    lang,
    page: params.get("from") ?? location.pathname,
    ...(question ? { questionId: question.id } : {}),
    ...(email.trim() ? { email: email.trim() } : {}),
  };
  const hasEndpoint = !!SITE.feedbackEndpoint;

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (honeypot) { setStatus("sent"); return; }
    const err = validateFeedback(input);
    setError(err);
    if (err) return;
    if (!hasEndpoint) {
      window.open(githubIssueUrl(input), "_blank", "noopener,noreferrer");
      return;
    }
    setStatus("sending");
    try {
      await sendFeedback(input);
      setStatus("sent");
      setMessage("");
    } catch {
      setError("send");
      setStatus("idle");
    }
  };

  if (status === "sent") {
    return (
      <div className="card center" role="status">
        <CircleCheck size={40} className="ok-text" aria-hidden="true" />
        <h1>{t("Cảm ơn bạn!", "Thank you!")}</h1>
        <p className="muted">{t("Góp ý đã được gửi. Mỗi phản hồi giúp ngân hàng đề chính xác hơn cho mọi người.", "Your feedback was sent. Every report makes the question bank more accurate for everyone.")}</p>
        <button type="button" className="btn" onClick={() => setStatus("idle")}>{t("Gửi góp ý khác", "Send more feedback")}</button>
      </div>
    );
  }

  return (
    <>
      <h1><MessageSquareHeart className="ico" size={28} aria-hidden="true" /> {t("Góp ý & báo lỗi", "Feedback & issue reports")}</h1>
      <p className="muted">
        {t(
          "Thấy đáp án sai, thông tin lỗi thời hay có ý tưởng hay? Gửi ngay — không cần đăng nhập. Chúng tôi đối chiếu mọi báo cáo với tài liệu chính thức của Anthropic trước khi sửa.",
          "Spotted a wrong answer, outdated info or have an idea? Send it — no login required. Every report is checked against Anthropic's official docs before we change anything.",
        )}
      </p>

      <form className="card form" onSubmit={onSubmit} noValidate>
        {question && (
          <div className="callout">
            <strong>{t("Câu hỏi:", "Question:")}</strong> <code>{question.id}</code>
            <div className="small muted" lang="en">{question.prompt.slice(0, 160)}{question.prompt.length > 160 ? "…" : ""}</div>
          </div>
        )}

        <fieldset>
          <legend>{t("Loại góp ý", "Type")}</legend>
          <div className="row wrap">
            {KINDS.map((k) => (
              <label key={k.id} className={`chip-radio${kind === k.id ? " on" : ""}`}>
                <input type="radio" name="kind" value={k.id} checked={kind === k.id} onChange={() => setKind(k.id)} />
                {pick(k.label, lang)}
              </label>
            ))}
          </div>
        </fieldset>

        <label className="field">
          <span>{t("Nội dung", "Message")} *</span>
          <textarea
            rows={7}
            value={message}
            maxLength={MAX_MESSAGE}
            onChange={(e) => setMessage(e.target.value)}
            placeholder={kind === "question"
              ? t("Đáp án nào sai/chưa rõ? Nếu có, dán link tài liệu chính thức làm căn cứ.", "Which answer is wrong or unclear? If you can, paste a link to the official docs that support it.")
              : t("Mô tả chi tiết…", "Describe it in detail…")}
            aria-invalid={error === "too-short" || error === "too-long"}
            required
          />
          <span className="tiny muted">{message.length}/{MAX_MESSAGE}</span>
        </label>

        {hasEndpoint && (
          <label className="field">
            <span>{t("Email (không bắt buộc — để nhận phản hồi)", "Email (optional — if you'd like a reply)")}</span>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" aria-invalid={error === "bad-email"} />
          </label>
        )}

        <label className="hp" aria-hidden="true">
          Website <input tabIndex={-1} autoComplete="off" value={honeypot} onChange={(e) => setHoneypot(e.target.value)} />
        </label>

        {error && <p className="callout bad" role="alert">{pick(ERRORS[error] ?? ERRORS.send!, lang)}</p>}

        <div className="row wrap">
          <button type="submit" className="btn primary" disabled={status === "sending"}>
            {hasEndpoint
              ? <><Send className="ico" size={18} aria-hidden="true" /> {status === "sending" ? t("Đang gửi…", "Sending…") : t("Gửi góp ý", "Send feedback")}</>
              : <><ExternalLink className="ico" size={18} aria-hidden="true" /> {t("Gửi qua GitHub Issues", "Submit via GitHub Issues")}</>}
          </button>
          {hasEndpoint && (
            <a className="btn ghost" href={githubIssueUrl(input)} target="_blank" rel="noreferrer noopener">
              <ExternalLink className="ico" size={18} aria-hidden="true" /> {t("Hoặc mở GitHub issue", "Or open a GitHub issue")}
            </a>
          )}
        </div>
        <p className="tiny muted mb-0">
          {hasEndpoint
            ? t("Email chỉ dùng để trả lời góp ý của bạn, không công khai.", "Your email is only used to reply to you and is never published.")
            : t("GitHub Issues là công khai — đừng ghi thông tin cá nhân. Cần tài khoản GitHub (miễn phí).", "GitHub Issues are public — don't include personal info. A free GitHub account is required.")}
        </p>
      </form>
    </>
  );
}
